import { useState } from 'react';

import useAuthenticationStatus from 'legacy/common/hooks/useAuthenticationStatus';
import computeCheckoutSessionUpdate from '../helpers/computeCheckoutSessionUpdate';
import { logEvent } from 'legacy/common/utils/amplitude';
import { EVENT_REDIRECT_ON_CHECKOUT_SESSION_COMPLETED_ERROR } from 'legacy/common/constants/events';
import useParams from 'legacy/common/hooks/useParams';
import {
  useConfirmMultiTierCheckoutSession,
  useStartMultiTierCheckoutSession,
  useStartUnauthenticatedMultiTierCheckoutSession,
  useUpdateMultiTierCheckoutSession,
  useUpdateUnauthenticatedMultiTierCheckoutSession
} from '../../../../hooks';
import {
  DiscountCodeDto,
  EventForUser,
  MultiTierCheckoutSession,
  StartCheckoutFn,
  UpdateCheckoutFn
} from '../../../../types';
import logTransactionToGA from '../helpers/logTransactionToGA';

const useMultiTierCheckoutSession = (
  event: EventForUser,
  discountData: DiscountCodeDto
) => {
  const params = useParams<{ t?: string }>();
  const { isAuthenticated } = useAuthenticationStatus();
  const [checkoutSession, setCheckoutSession] =
    useState<MultiTierCheckoutSession>();

  const startCheckoutSession = useStartMultiTierCheckoutSession();
  const startUnauthenticatedCheckoutSession =
    useStartUnauthenticatedMultiTierCheckoutSession();
  const updateCheckoutSession = useUpdateMultiTierCheckoutSession();
  const updateUnauthenticatedCheckoutSession =
    useUpdateUnauthenticatedMultiTierCheckoutSession();
  const confirmCheckoutSession = useConfirmMultiTierCheckoutSession();

  /**
   * Starts the checkout process for a given event.
   */
  const startCheckout: StartCheckoutFn = (eventId) => {
    if (!eventId) {
      return;
    }

    const startCheckoutMutation = isAuthenticated()
      ? startCheckoutSession
      : startUnauthenticatedCheckoutSession;

    startCheckoutMutation.mutate(eventId, {
      onSuccess: (data) => {
        setCheckoutSession(data);
      }
    });
  };

  /**
   * Update the checkout session with given parameters and options.
   */
  const updateCheckout: UpdateCheckoutFn = (updateData) =>
    new Promise((resolve, reject) => {
      if (!checkoutSession?.paymentIntentId || !event?._id) {
        reject(
          new Error('paymentIntentId not provided or event id not provided')
        );
      }

      const isUserAuthenticated = isAuthenticated();

      const updateCheckoutMutation = isUserAuthenticated
        ? updateCheckoutSession
        : updateUnauthenticatedCheckoutSession;

      startCheckoutSession.reset();
      confirmCheckoutSession.reset();

      updateCheckoutMutation.mutate(
        {
          eventId: event._id,
          paymentIntentId: checkoutSession?.paymentIntentId,
          body: computeCheckoutSessionUpdate(updateData, isUserAuthenticated)
        },
        {
          onSuccess: (data) => {
            setCheckoutSession(data);
            resolve(data);
          },
          onError: (error) => {
            reject(error);
          }
        }
      );
    });

  /**
   * Confirms the checkout session with the provided data.
   * Also logs the event in Google Analytics if transaction was successful.
   */
  const confirmCheckout = (confirmData: { allWaiversAccepted: boolean }) =>
    new Promise((resolve, reject) => {
      if (!checkoutSession?.paymentIntentId || !event?._id) {
        reject(
          new Error('paymentIntentId not provided or event id not provided')
        );
      }

      startCheckoutSession.reset();
      updateCheckoutSession.reset();

      confirmCheckoutSession.mutate(
        {
          eventId: event._id,
          paymentIntentId: checkoutSession.paymentIntentId,
          isWaiverAccepted: confirmData?.allWaiversAccepted,
          promoterCode: discountData?.code || undefined,
          ...(params.t && { trackingId: params.t })
        },
        {
          onSuccess: () => {
            // Add transaction to google analytics
            logTransactionToGA(checkoutSession, event, discountData);

            resolve(null);
          },
          onError: (err) => {
            if (err.code === 'checkoutSessionCompleted') {
              logEvent(EVENT_REDIRECT_ON_CHECKOUT_SESSION_COMPLETED_ERROR);
              return resolve(null);
            }

            return reject(err);
          }
        }
      );
    });

  return {
    checkoutSession,
    isLoadingCheckout:
      startCheckoutSession.isLoading ||
      startUnauthenticatedCheckoutSession.isLoading ||
      updateCheckoutSession.isLoading ||
      updateUnauthenticatedCheckoutSession.isLoading ||
      confirmCheckoutSession.isLoading,
    isStartingCheckout:
      startCheckoutSession.isLoading ||
      startUnauthenticatedCheckoutSession.isLoading,
    isUpdatingCheckout:
      updateCheckoutSession.isLoading ||
      updateUnauthenticatedCheckoutSession.isLoading,
    isConfirmingCheckout: confirmCheckoutSession.isLoading,
    confirmCheckoutError: confirmCheckoutSession.error,
    checkoutError:
      startCheckoutSession.error ||
      updateCheckoutSession.error ||
      startUnauthenticatedCheckoutSession.error ||
      updateUnauthenticatedCheckoutSession.error ||
      confirmCheckoutSession.error,
    isCheckoutSuccess: confirmCheckoutSession.isSuccess,
    isCheckoutDisabled: Boolean(startCheckoutSession.error),
    startCheckout,
    updateCheckout,
    confirmCheckout
  };
};

export default useMultiTierCheckoutSession;
